/**
 * Which wizard steps you may jump to, and which arrived already answered.
 *
 * Pure on purpose. The rules used to live inside CreateBuildingProjectWizardProvider, where they could
 * not be tested: importing that store pulls the BuildingProject barrel, which reaches the Apollo client
 * and the 3D viewer, neither of which constructs under `environment: "node"`.
 */

/** The shape these rules need from a step: an id, and whether the project satisfies it. */
export type GatableStep<TProject> = {
  id: string;
  valid: (project: TProject) => boolean;
};

/**
 * The step immediately after the latest done one, in overview order.
 *
 * With nothing done this is the FIRST step, which is what makes the flow start somewhere reachable:
 * `indexOf(undefined)` is -1, so `-1 + 1` lands on index 0. That fallback is load-bearing, not a
 * coincidence.
 *
 * Returns undefined once the last step is done, rather than the previous `flow[i + 1].id` which threw
 * on exactly that case.
 */
export const nextStepAfterDoneId = (stepsDone: string[], overviewIds: string[]): string | undefined => {
  const latestDone = stepsDone[stepsDone.length - 1];
  return overviewIds[overviewIds.indexOf(latestDone) + 1];
};

/**
 * Creating a project is earned in order: a step is reachable only if it is already done, or it is the
 * one straight after the latest done step.
 *
 * Deliberately NOT widened to "every earlier step is valid". A step arriving valid does not mean the
 * user has been through it, and the start-project dialog makes that distinction real — see
 * preAnsweredStepIds.
 */
export const canJumpToStep = (step: string, stepsDone: string[], overviewIds: string[]): boolean =>
  stepsDone.includes(step) || step === nextStepAfterDoneId(stepsDone, overviewIds);

/**
 * Steps whose answers the start-project dialog can actually supply.
 *
 * An allowlist rather than "every step that reports valid", because validity does not mean answered:
 * Standard values, Special conditions and Speckle all return a constant `true` from `valid()`, they have
 * nothing to validate, so a validity-only rule would light up most of the rail as pre-answered and mean
 * nothing.
 *
 * Empty today. Calculation prerequisites was the one step that ever qualified, and it no longer exists as
 * a create step to qualify: its inputs (consideration period, Denmark's limit-value fields, stage editing
 * for Custom) are now a card on Project details, the first step of every create flow. There is nothing
 * AFTER step one left for a dialog answer to pass through on the way to, which is what this list is for
 * (see advanceThroughPreAnswered).
 *
 * Project details itself is deliberately not on this list, even though the dialog now supplies most of
 * what it asks (name, building type, project status, calculation type). Pre-answered and skippable are
 * different claims: this list names steps a dialog answer lets Continue walk PAST, and Project details is
 * the step Continue always lands ON first, so those answers get a review screen instead of disappearing
 * into a step nobody sees. `basicsPreAnswered` in ProjectDetails.vue is the display fact that this file's
 * "valid but not visited" distinction protects: the step reports valid without being marked done, and
 * stays reachable rather than jumped over.
 *
 * The mount-time write this list used to guard is still real, and still runs, now on Project details'
 * mount instead of a dedicated step's, because CalculationTypeForm is what runs it, not the step that used
 * to wrap it alone. Unaffected by the fold: BR18 (2023), Low emission (2023) and Reduction roadmap carry a
 * flat `threshold`, so the dialog seeds thresholdLimitWithoutExtra directly and there is nothing left to
 * compute. BR18 (2025) and Low emission (2025) carry NO flat threshold, 18 thresholdByBuildingType rows
 * each, so thresholdLimitWithoutExtra is null when the dialog hands off, and CalculationTypeForm's own
 * mount-time `updateThresholds` resolves it: measured, Denmark + BR18 (2025) + Handel arrives with a null
 * threshold and leaves with 7.5, plus a 1.5 construction limit, both fields disabled. It does not need the
 * areas from the Building step either: `getThresholds` matches `totalArea <= maxTotalArea`, which holds at
 * area 0, so the area only chooses between bands where a building type has several, which is also why
 * running it on step one, ahead of Building, loses nothing.
 *
 * Custom and Level(s) are the other shape: no flat threshold and no thresholdByBuildingType rows (see
 * calculationLimitValue.ts), so `updateThresholds` never fires for them (its own guard is
 * `showConstruction || hasBuildingTypeThresholds`) and the limit value field stays null, editable, exactly
 * as it did on the step this replaced. Writing nothing is correct here, not a gap.
 */
export const DIALOG_ANSWERABLE_STEP_IDS: readonly string[] = [];

/**
 * Steps the project already satisfies without having been visited.
 *
 * Stays generic and testable against an explicit allowlist (see the tests below) even though
 * DIALOG_ANSWERABLE_STEP_IDS is empty today: the fold that emptied it was a step disappearing into
 * Project details, not a reason for this mechanism to stop existing. It stays as latent capability for a
 * future step that arrives valid from dialog answers without being the one Continue always lands on
 * first.
 *
 * Kept apart from `stepsDone` because navigation reads the LAST entry of that list to decide what is
 * unlocked (see nextStepAfterDoneId). Putting a mid-flow step in there would unlock the step after IT
 * and let the user skip the unanswered ones in front, so this list is for display only, and
 * canJumpToStep never consults it.
 */
export const preAnsweredStepIds = <TProject>(
  steps: GatableStep<TProject>[],
  project: TProject,
  stepsDone: string[],
  answerableIds: readonly string[] = DIALOG_ANSWERABLE_STEP_IDS
): string[] =>
  steps
    .filter((step) => answerableIds.includes(step.id) && !stepsDone.includes(step.id) && step.valid(project))
    .map((step) => step.id);

/**
 * Where Continue lands, having passed through any steps the dialog already answered.
 *
 * Walks forward from the step just left, collecting steps that are both on the answerable list AND report
 * valid, and stops on the first that is not. Returns the step to land on and the ones crossed on the way.
 *
 * The invariant this is built around: `passedIds` are only steps BETWEEN the current one and the landing
 * one, so folding them into `stepsDone` keeps that list a prefix of the path actually walked. Seeding
 * `stepsDone` from `preAnsweredStepIds` at init would not — `canJumpToStep` unlocks from the LAST entry, so
 * a mid-flow step marked done unlocks the step after IT and lets the user walk past everything in front.
 * See preAnsweredStepIds. Crossing a step while moving forward is exactly what pressing Continue on it
 * would have done, which is why this direction is safe and that one is not.
 *
 * `landOnId` is undefined only if every remaining step is answered, which the flow's last steps (Speckle,
 * Project created) can never be — neither is on the answerable list. Returned rather than assumed away
 * because the caller must not index off the end, which is the bug this file's other helpers exist to fix.
 */
export const advanceThroughPreAnswered = <TProject>(
  steps: GatableStep<TProject>[],
  fromId: string,
  project: TProject,
  answerableIds: readonly string[] = DIALOG_ANSWERABLE_STEP_IDS
): { landOnId: string | undefined; passedIds: string[] } => {
  const fromIndex = steps.findIndex((step) => step.id === fromId);
  if (fromIndex === -1) return { landOnId: undefined, passedIds: [] };

  const passedIds: string[] = [];

  for (let index = fromIndex + 1; index < steps.length; index += 1) {
    const step = steps[index];
    const alreadyAnswered = answerableIds.includes(step.id) && step.valid(project);

    if (!alreadyAnswered) return { landOnId: step.id, passedIds };

    passedIds.push(step.id);
  }

  return { landOnId: undefined, passedIds };
};
